import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { JwtInterface } from 'src/services/Auth/Auth.types';
import { isReactNativeWebView } from 'src/utils/webview-detection';
import * as ug from './user-guiding';
import { shouldRequestTrustpilotReview } from './trustpilot-review-guide.logic';

/**
 * UserGuiding guide that asks the client to leave a TrustPilot review.
 *
 * Must stay a `number`. `previewGuide` resolves the guide with a strict
 * `guides.find(g => g.id === guideId)` against a store whose ids are numbers,
 * so a string id matches nothing and the call becomes a silent no-op — it only
 * logs "You are trying to start a guide that does not exist in current
 * container" to the console. That is exactly how this shipped on 2026-07-30:
 * the ask reached the SDK on every 4-5 star rating and no guide ever rendered.
 */
export const TRUSTPILOT_REVIEW_GUIDE_ID = 168973;

/**
 * Viewer identity, read from the JWT rather than `AuthUtils.getPayload()`.
 *
 * `getPayload()` returns null once the token is *client-side* expired, which
 * would silently turn an impersonated session into an ordinary client one and
 * drop the impersonation guard. The API still accepts such a token (a fast local
 * clock, or the window before the visibility-change refresh runs), so the rating
 * succeeds and we would ask for a review under the client's identity. Decoding
 * directly keeps `impersonator` readable; an undecodable token yields no
 * identity at all, which the caller treats as ineligible.
 */
const getViewerIdentity = (): JwtInterface | null => {
  const { token } = AuthUtils.getJwt();
  if (!token) return null;

  try {
    return AuthUtils.decode(token);
  } catch {
    return null;
  }
};

/**
 * Ask the client for a TrustPilot review after they rated a revision.
 *
 * Returns whether the ask reached UserGuiding — not whether the guide was
 * shown, which UserGuiding decides from the client's own history.
 */
export const requestTrustpilotReview = (rating: number): boolean => {
  const identity = getViewerIdentity();

  const shouldRequest = shouldRequestTrustpilotReview({
    rating,
    userType: identity?.user_type,
    // No readable token means we cannot rule out impersonation, so fail closed.
    isImpersonated: identity ? !!identity.impersonator : true,
    isWebView: isReactNativeWebView(),
  });

  if (!shouldRequest) return false;

  // `checkHistory: true` is required, not optional. By default `previewGuide`
  // fires every time it is called and ignores the client's history — the guide
  // would pop on every qualifying rating. With it, the frequency rules
  // configured on the guide in the UserGuiding dashboard are respected, so CS
  // can change the cadence without involving engineering.
  //
  // It does more than replay history: with `checkHistory` the SDK also applies
  // the guide's own segment and page-targeting filters and requires user
  // identification to have completed. So the dashboard config DOES gate this
  // ask — a page-targeting rule that excludes the revisions URL, or a segment
  // the client falls outside of, suppresses it. UserGuiding's Troubleshooter
  // reports each of those conditions per guide and is the place to check first.
  //
  // This is also why we do not use the URL tag trigger (`?__ug__=168973`): it
  // has no equivalent of `checkHistory`, so it bypasses frequency rules
  // entirely, and it needs a page refresh to fire.
  const dispatched = ug.call('previewGuide', TRUSTPILOT_REVIEW_GUIDE_ID, { checkHistory: true });

  // Still dispatched above even when the SDK script has not loaded — the stub
  // queues the call for replay, so the client may yet be asked. But an
  // unqueued/blocked script never delivers it, so only report success once the
  // SDK is actually live. Callers use the return value for analytics, and
  // over-reporting asks that never happened would corrupt the ask→review rate.
  return dispatched && ug.isLoaded();
};
